import React, { useEffect, useState } from 'react';
import { Product } from '../../../types/product';
import { productToDcmViewModel } from '../../../utils/knowledgeDcmAdapter';
import monitoringService from '../../../services/api/knowledge/monitoringService';
import { MonitoringCompetitor } from '../../../types/knowledgeMonitoring';

interface BasicInfoTabProps {
  product?: Product | null;
  /** 「編集」ボタン押下時に既存の商品編集モーダル（ProductCreateForm）を開く。 */
  onEdit?: () => void;
}

const CARD_SHADOW: React.CSSProperties = { boxShadow: '0 0 18px 0 rgba(31,23,18,.06)' };

const GHOST_BTN =
  'inline-flex items-center gap-[5px] rounded-lg border border-border bg-white px-[11px] py-[5px] text-[12px] font-semibold text-gray-600 transition-colors hover:border-[#AEB5DE] hover:bg-[#F5F6FC] hover:text-[#4F5BB5] disabled:cursor-not-allowed disabled:opacity-50';

// ②: ポジショニングマップの競合ドット配置（簡易な固定プリセット。実座標データが無いため
// 「重ならない程度に散らす」ことだけを目的にした決定的な位置）。自社ドット（left:72%,top:34%）
// と被らないよう避けてある。競合が多い場合はこのプールを周回して割り当てる。
const COMPETITOR_MAP_POSITIONS: Array<{ left: number; top: number }> = [
  { left: 35, top: 46 },
  { left: 48, top: 66 },
  { left: 60, top: 52 },
  { left: 24, top: 28 },
  { left: 66, top: 76 },
  { left: 40, top: 20 },
];

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({ product, onEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [competitors, setCompetitors] = useState<MonitoringCompetitor[]>([]);

  // ②: ポジショニングマップに競合の企業名を表示するため、競合情報タブと同じAPIで取得する。
  useEffect(() => {
    let isActive = true;
    const productId = product?.id;
    if (!productId) {
      setCompetitors([]);
      return undefined;
    }

    monitoringService
      .getCompetitors(productId)
      .then((result) => {
        if (isActive) setCompetitors(result);
      })
      .catch((error) => {
        console.error('[BasicInfoTab] Failed to load competitors for positioning map', error);
        if (isActive) setCompetitors([]);
      });

    return () => {
      isActive = false;
    };
  }, [product?.id]);

  if (!product) {
    return (
      <div className="flex h-64 items-center justify-center text-gray-500">
        商品を選択してください
      </div>
    );
  }

  const meta = product.extended_metadata;
  const productInfo = meta?.product;
  const pricingOffer = meta?.pricing_offer;
  const positioning = meta?.positioning;
  const viewModel = productToDcmViewModel(product);
  const productImages = product.product_images || product.images || [];
  const previewImages = productImages.slice(0, 6);
  const trustProof = (positioning?.trust_proof || [])
    .map((proof: any) =>
      typeof proof === 'string'
        ? { text: proof, src: undefined as string | undefined, url: undefined as string | undefined }
        : { text: proof?.statement, src: proof?.source, url: proof?.url },
    )
    .filter((proof) => Boolean(proof.text))
    .slice(0, 5);
  // USP（提供価値）は①: #633の改行正規化/line-clampを元に戻し、生成された文をそのまま表示する。
  const usp = productInfo?.usp || (positioning as any)?.usp;
  // ②: 「◯◯なら、{商品名}」の「◯◯」は、USPと商品内容を統合した専用フィールド
  // （positioning.statement_lead）を優先使用する。ヒーロー表示の一文なので改行・連続空白のみ
  // 正規化する。未生成の既存データはstatement_leadが無いため、旧フォールバック（usp||tagline||name）
  // へ自動的に落ちる（表示互換・バックフィル不要）。
  const normalizeLead = (value?: string): string | undefined =>
    value ? String(value).replace(/\s*\n+\s*/g, ' ').replace(/\s{2,}/g, ' ').trim() : value;
  const positioningStatement =
    normalizeLead(positioning?.statement_lead) || usp || normalizeLead(productInfo?.tagline) || product.name;
  const pod = positioning?.pod || [];
  const pop = positioning?.pop || [];
  const sources = viewModel.source;
  const open = expanded;

  const specRows: Array<[string, React.ReactNode]> = [
    ['商品名', product.name || productInfo?.name],
    ['ブランド', productInfo?.brand || product.metadata?.brand],
    ['カテゴリ', productInfo?.category || product.categories?.map((category) => category.name).join(', ')],
    ['市場タイプ', productInfo?.market_type],
    ['販売モデル', productInfo?.sales_model],
    ['キャッチコピー', productInfo?.tagline],
    ['説明', productInfo?.description || product.description],
  ];
  if (productInfo?.use_scenarios) specRows.push(['利用シーン', productInfo.use_scenarios]);
  if (productInfo?.variations) specRows.push(['バリエーション', productInfo.variations]);

  const priceRows: Array<[string, React.ReactNode]> = [
    ['メインCTA', pricingOffer?.main_cta],
    ['価格表示', pricingOffer?.price_visibility],
    ['価格詳細', pricingOffer?.price_details],
    ['キャンペーン', pricingOffer?.offers_campaigns],
  ];

  return (
    <div className="font-sans text-dark">
      {/* 見出し行 */}
      <div className="mt-2 flex items-center gap-3">
        <h2 className="flex items-center gap-2 text-lg font-bold">
          <span className="material-icons-round text-[21px] text-primary">description</span>
          基本情報
          <span className="help-tip">
            <span className="material-icons-round">help_outline</span>
            <span className="tip-body">
              <b>この商品そのもの。</b>スペック・価格に加えて「選ばれる理由(USP)」もここ。誰に売るかで変わらない土台。これを材料に、誰に当てるかを決めるのが戦略マップ。
            </span>
          </span>
        </h2>
        <div className="flex-1" />
        {/* 「編集」は既存の商品編集モーダル（ProductCreateForm）を開く。旧DCM再設計で配線が失われ
            無反応になっていたため、親から渡される onEdit に再接続する。 */}
        <button type="button" className={GHOST_BTN} onClick={() => onEdit?.()} disabled={!onEdit}>
          <span className="material-icons-round text-[14px]">edit</span>編集
        </button>
      </div>

      <div className="mt-4">
        {/* 商品画像ギャラリー */}
        <div className="mb-4 rounded-2xl bg-white p-5" style={CARD_SHADOW}>
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[13px] font-bold">
              <span className="material-icons-round text-[17px] text-primary">photo_library</span>
              商品画像
              <span className="text-[11px] font-medium text-gray-400">{productImages.length}枚</span>
            </div>
            <span className="flex items-center gap-1 text-[12px] font-semibold text-primary">
              商品画像を管理
              <span className="material-icons-round text-[14px]">arrow_forward</span>
            </span>
          </div>
          {previewImages.length > 0 ? (
            <div
              className="grid gap-[10px]"
              // タイル幅は枚数に依存させない（モック: 1080px幅で約6列の小タイル）
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}
            >
              {previewImages.map((image: any, index) => (
                <div
                  key={image.id || index}
                  className="relative overflow-hidden rounded-[13px] bg-white"
                  style={{ aspectRatio: '4 / 3', boxShadow: '0 1px 4px rgba(31,23,18,.08)' }}
                >
                  {/* ⑧: 縦長/横長画像でも見切れないよう object-contain + 白背景で枠内に全体表示する。 */}
                  <img
                    src={image.url}
                    alt={image.alt || product.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full bg-white object-contain"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex min-h-[72px] w-full items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-[#d9d6cd] bg-[#fbfaf7] text-xs font-bold text-gray-400">
              <span className="material-icons-round text-[20px] text-gray-400">image</span>
              <span>商品画像が登録されていません</span>
            </div>
          )}
        </div>

        {/* 折りたたみトグル（商品スペック・料金/オファー） */}
        <div className="overflow-hidden rounded-2xl bg-white" style={CARD_SHADOW}>
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="flex w-full items-center gap-[13px] bg-white px-[18px] py-[15px] text-left transition-colors hover:bg-[#FAFAF8]"
          >
            <span
              className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[11px] text-white"
              style={{ background: 'linear-gradient(135deg,#616FC9,#9675C9)' }}
            >
              <span className="material-icons-round text-[22px]">apartment</span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-[14.5px] font-extrabold">
                {product.name}
                <span
                  className="rounded-full px-2 py-[2px] text-[9.5px] font-extrabold tracking-[.04em]"
                  style={{ background: '#F2F1ED', color: '#8A90A4' }}
                >
                  基本情報
                </span>
              </span>
              <span
                className="mt-[3px] block max-w-[600px] overflow-hidden text-ellipsis whitespace-nowrap text-[11.5px]"
                style={{ color: '#8A90A4' }}
              >
                商品スペック・料金/オファー
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-[2px] text-[11.5px] font-bold text-primary">
              {open ? '閉じる' : '開く'}
              <span className="material-icons-round text-[19px]">{open ? 'expand_less' : 'expand_more'}</span>
            </span>
          </button>
        </div>

        {open && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white p-5" style={CARD_SHADOW}>
              <div className="mb-2 flex items-center gap-1.5 text-[13px] font-bold">
                <span className="material-icons-round text-[17px] text-gray-400">apartment</span>商品スペック
              </div>
              <dl>
                {specRows.map(([label, value]) => (
                  <InfoRow key={label} label={label} value={value} />
                ))}
              </dl>
            </div>
            <div className="rounded-2xl bg-white p-5" style={CARD_SHADOW}>
              <div className="mb-2 flex items-center gap-1.5 text-[13px] font-bold">
                <span className="material-icons-round text-[17px] text-gray-400">sell</span>料金・オファー
              </div>
              <dl>
                {priceRows.map(([label, value]) => (
                  <InfoRow key={label} label={label} value={value} />
                ))}
              </dl>
            </div>
          </div>
        )}

        {/* 選ばれる理由 */}
        <div className="mt-6">
          <h3 className="flex items-center gap-2 text-base font-bold">
            <span className="material-icons-round text-[19px] text-primary">military_tech</span>
            選ばれる理由
          </h3>

          <div
            className="relative mt-3 overflow-hidden rounded-2xl p-7 text-white"
            style={{ background: 'linear-gradient(120deg,#616FC9,#9675C9)' }}
          >
            <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-widest opacity-80">
              <span className="material-icons-round text-[14px]">my_location</span>取るべきポジショニング
            </div>
            <p className="mt-2.5 max-w-[760px] text-[22px] font-extrabold leading-[1.5] tracking-[-0.01em]">
              {positioningStatement}
              <span className="opacity-90">なら、</span>
              <br />
              <span className="mt-[2px] inline-block">{product.name}</span>
            </p>
          </div>

          {(usp || trustProof.length > 0) && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              {usp && (
                <div className="rounded-2xl bg-white p-5" style={CARD_SHADOW}>
                  <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold tracking-wide text-gray-400">
                    <span className="material-icons-round text-[15px] text-primary">bolt</span>USP（提供価値）
                  </div>
                  <p className="text-[14px] font-bold leading-relaxed text-dark">{usp}</p>
                </div>
              )}
              {trustProof.length > 0 && (
                <div className="rounded-2xl bg-white p-5" style={CARD_SHADOW}>
                  <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold tracking-wide text-gray-400">
                    <span className="material-icons-round text-[15px] text-success">verified</span>
                    RTB（信じる根拠）
                    <span className="text-[10px] font-medium text-gray-300">最大5件</span>
                  </div>
                  <ul className="flex flex-col gap-[11px]">
                    {trustProof.map((proof, index) => (
                      <li key={`${proof.text}-${index}`} className="flex items-center gap-[9px]">
                        <span className="h-[6px] w-[6px] shrink-0 rounded-full bg-success" />
                        <div className="min-w-0 flex-1 text-[13px] font-semibold leading-[1.45] text-gray-700">
                          {proof.text}
                        </div>
                        {proof.url && (
                          <a
                            href={proof.url}
                            target="_blank"
                            rel="noreferrer"
                            title={proof.src ? `出典: ${proof.src}` : undefined}
                            className="inline-flex shrink-0 items-center text-gray-400 transition-colors hover:text-primary"
                          >
                            <span className="material-icons-round text-[15px]">open_in_new</span>
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 rounded-2xl bg-white p-5" style={CARD_SHADOW}>
            <div className="mb-3 flex items-center gap-1.5 text-[13px] font-bold">
              <span className="material-icons-round text-[17px] text-primary">scatter_plot</span>
              ポジショニングマップ
              <span className="text-[11px] font-medium text-gray-400">（競合情報をもとに自動生成）</span>
            </div>
            <div className="relative h-60 rounded-xl border border-[#e7e9f0] bg-[#fafbfe]">
              <div className="absolute left-[9%] right-[9%] top-1/2 h-px bg-[#dcdce2]" />
              <div className="absolute bottom-[9%] left-1/2 top-[9%] w-px bg-[#dcdce2]" />
              <span className="absolute left-4 top-3 text-[10px] font-bold text-gray-400">情緒価値</span>
              <span className="absolute bottom-3 right-4 text-[10px] font-bold text-gray-400">機能価値</span>
              <span className="absolute h-[26px] w-[26px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary shadow-[0_0_0_7px_rgba(97,111,201,0.18)]" style={{ left: '72%', top: '34%' }} />
              <span className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] font-extrabold text-primary" style={{ left: '72%', top: '22%' }}>{product.name}</span>
              {/* ②: 競合の点にも企業名を小さく表示する（自社より控えめな色・サイズ） */}
              {competitors.slice(0, COMPETITOR_MAP_POSITIONS.length).map((competitor, index) => {
                const position = COMPETITOR_MAP_POSITIONS[index % COMPETITOR_MAP_POSITIONS.length];
                const left = `${position.left}%`;
                const top = `${position.top}%`;
                return (
                  <React.Fragment key={competitor.id}>
                    <span className="absolute h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-400" style={{ left, top }} />
                    <span className="pointer-events-none absolute max-w-[90px] -translate-x-1/2 -translate-y-1/2 overflow-hidden text-ellipsis whitespace-nowrap text-[9px] font-semibold text-gray-400" style={{ left, top: `calc(${top} - 12px)` }}>
                      {competitor.name}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {(pod.length > 0 || pop.length > 0) && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              {pod.length > 0 && (
                <div className="rounded-2xl bg-white p-5" style={CARD_SHADOW}>
                  <div className="mb-3 flex items-center gap-1.5 text-[13px] font-bold">
                    <span
                      className="flex h-5 w-5 items-center justify-center rounded text-[11px] font-bold text-success"
                      style={{ background: 'rgba(68,194,164,.1)' }}
                    >
                      差
                    </span>
                    自社だけの差別化ポイント（POD）
                  </div>
                  <BulletList items={pod} />
                </div>
              )}
              {pop.length > 0 && (
                <div className="rounded-2xl border border-warm-border bg-warm-card p-5">
                  <div className="mb-3 flex items-center gap-1.5 text-[13px] font-bold text-gray-600">
                    <span className="material-icons-round text-[16px] text-gray-400">inventory_2</span>
                    競合と共通しているポイント（POP）
                  </div>
                  <BulletList items={pop} />
                </div>
              )}
            </div>
          )}

          {sources.length > 0 && (
            <div className="mt-4 rounded-2xl bg-white p-5" style={CARD_SHADOW}>
              <div className="mb-1 flex items-center gap-1.5 text-[13px] font-bold">
                <span className="material-icons-round text-[17px] text-gray-400">format_quote</span>
                引用元（{sources.length}件）
                <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-medium text-gray-400">
                  <span className="material-icons-round text-[12px]">lock</span>出典・自動取得
                </span>
              </div>
              {sourcesExpanded && (
                <div className="flex flex-col">
                  {sources.map((source, index) => {
                    const label = source.title || source.source || source.url || '-';
                    return (
                      <div
                        key={`${source.url || source.title || index}`}
                        className="border-b border-[#F1EFE9] py-[9px] last:border-b-0"
                      >
                        {source.statement && (
                          <div className="text-[13px] leading-[1.6] text-gray-700">{source.statement}</div>
                        )}
                        {source.url ? (
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-[3px] inline-flex items-center gap-[2px] text-[11px] font-semibold text-primary hover:underline"
                          >
                            {label}
                            <span className="material-icons-round text-[12px]">open_in_new</span>
                          </a>
                        ) : (
                          <div className="mt-[3px] text-[11px] font-semibold text-gray-500">{label}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                onClick={() => setSourcesExpanded((value) => !value)}
                className="mt-[10px] inline-flex items-center gap-[3px] text-[11.5px] font-bold text-primary"
              >
                <span className="material-icons-round text-[16px]">
                  {sourcesExpanded ? 'expand_less' : 'expand_more'}
                </span>
                {sourcesExpanded ? '閉じる' : `出典をすべて表示（${sources.length}件）`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start gap-3 border-b border-[#F4F3EF] py-[9px] text-[13px] last:border-b-0">
    <dt className="w-[116px] shrink-0 pt-[2px] text-[12px] font-medium text-gray-400">{label}</dt>
    <dd className="m-0 flex-1 leading-[1.7] text-gray-700">{value || '-'}</dd>
  </div>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="space-y-1 text-[13px] text-gray-700">
    {items.map((item, index) => (
      <li key={`${item}-${index}`} className="flex items-start">
        <span className="mr-2 text-gray-300">•</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default BasicInfoTab;
